import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, ClientSummary } from '@/api/client';

interface ClientContextType {
  clients: ClientSummary[];
  selectedClient: ClientSummary | null;
  isLoading: boolean;
  error: string | null;
  selectClient: (clientId: string) => void;
  refreshClients: () => Promise<void>;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

export function ClientProvider({ children }: { children: ReactNode }) {
  const [selectedClient, setSelectedClient] = useState<ClientSummary | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const response = await apiClient.clients.list();
      return response.clients;
    },
    staleTime: 5 * 60 * 1000,
  });

  const clients = data || [];

  // Auto-select preferred default client if none selected
  // Priority: 1) Colas Mexico (Generic), 2) First client in list
  useEffect(() => {
    if (clients.length > 0 && !selectedClient) {
      const preferredClient = clients.find(c => c.client_id === 'colas-mexico');
      setSelectedClient(preferredClient || clients[0]);
    }
  }, [clients, selectedClient]);

  const selectClient = useCallback((clientId: string) => {
    const client = clients.find(c => c.client_id === clientId);
    if (client) {
      setSelectedClient(client);
    }
  }, [clients]);

  const refreshClients = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const value: ClientContextType = {
    clients,
    selectedClient,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to load clients') : null,
    selectClient,
    refreshClients,
  };

  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
}

export function useClient() {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
}
